import {Dispatch, SetStateAction, useEffect, useMemo, useState} from 'react';
import {useDebounce, useUpdateEffect} from 'react-use';
import {Alert, AlertTitle, Button, Card, Icon, RadioGroup, Select} from '@ezcater/tapas';
import {faBuilding, faBuildings} from '@fortawesome/pro-regular-svg-icons';
import {Form, useField, useFormikContext} from 'formik';
import useTranslation from 'next-translate/useTranslation';
import {twJoin} from 'tailwind-merge';

import {useDerivedMenuData} from '@/components/features/menu/hooks';
import useAnalyticsSearchId from '@/components/features/search/content/SearchPageRoot/useAnalyticsSearchId';
import AddressSearchAutocomplete from '@/components/global/address/AddressSearchAutocomplete';
import {type TrackEvent} from '@/components/global/layout/EventBar/types';
import useOrderAccounts, {OrderAccount} from '@/components/global/layout/EventBar/useOrderAccounts';
import {useFulfillmentDetailState} from '@/components/global/providers/FulfillmentDetailStateProvider';
import {useStoreNextRouter} from '@/components/global/providers/StoreNextRouter';
import {FulfillmentDetailStrategy} from '@/graphql/types';
import useIsAppBarLg from '@/hooks/useIsAppBarLg';
import useSavedAddressSearch from '@/hooks/useSavedAddressSearch';
import useTracking from '@/hooks/useTracking';
import {getFullAddress} from '@/utils';
import {
  type GeocodedAddress,
  type GeocodedAddressResult,
  type SavedAddress,
} from '@/utils/googleAutocomplete';
import {FulfillmentDetailFormProps, ValidateAddressFunction} from './EventBar';

type LocationFormValues = {
  addressString: string;
  eventOrderType: FulfillmentDetailStrategy;
  geocodedAddressResult: GeocodedAddressResult | null;
  googlePlace: google.maps.places.AutocompletePrediction | null;
  orderAccount?: OrderAccount | null;
  savedAddress: SavedAddress | null;
};

type LocationProps = {
  multipleAddresses: GeocodedAddress[] | null;
  onSelectOpenChange?: (open: boolean) => void;
  setMultipleAddresses: Dispatch<SetStateAction<GeocodedAddress[] | null>>;
  setShowIncompleteAddressWarning: Dispatch<SetStateAction<boolean>>;
  showIncompleteAddressWarning: boolean;
  trackEvent: TrackEvent;
  validateAddress: ValidateAddressFunction;
};

const AddressUpdateListener: React.FC<{
  setMultipleAddresses: Dispatch<SetStateAction<GeocodedAddress[] | null>>;
  setShowIncompleteAddressWarning: Dispatch<SetStateAction<boolean>>;
}> = ({setMultipleAddresses, setShowIncompleteAddressWarning}) => {
  const [addressString] = useField('addressString');
  const [savedAddress] = useField('savedAddress');
  const {setFieldValue} = useFormikContext<LocationFormValues>();

  // When the address input changes
  useUpdateEffect(() => {
    // Reset multiple addresses and alerts
    setMultipleAddresses(null);
    setShowIncompleteAddressWarning(false);

    // And it's different from any selected saved address, reset the saved address
    if (savedAddress?.value && savedAddress.value.secondaryText !== addressString?.value) {
      setFieldValue('savedAddress', null);
    }
  }, [addressString.value]);

  return null;
};

const Location: React.FC<LocationProps> = ({
  multipleAddresses,
  onSelectOpenChange,
  setMultipleAddresses,
  setShowIncompleteAddressWarning,
  showIncompleteAddressWarning,
  trackEvent,
  validateAddress,
}) => {
  const {t} = useTranslation('app-bar');
  const {isOnMenuPage, isOnSearchPage} = useStoreNextRouter();
  const {fulfillmentDetailClickHandler, FulfillmentDetailStates, fulfillmentDetail} =
    useFulfillmentDetailState();
  const {shouldRenderSelectOrderAccount, availableCorpAccountOptions} = useOrderAccounts();
  // `Select.Value` renders the raw value unless `Select.Root` is given the item labels.
  const corpAccountSelectItems = useMemo(
    () => availableCorpAccountOptions.map(({id, label}) => ({label, value: id})),
    [availableCorpAccountOptions],
  );
  const {setFieldValue, values, isSubmitting, status} =
    useFormikContext<FulfillmentDetailFormProps>();
  const hasMultipleAddresses = Boolean(multipleAddresses && multipleAddresses.length > 0);
  const cta = isOnMenuPage
    ? t('eventBar.eventLocation.cta.menu')
    : isOnSearchPage
      ? t('eventBar.eventLocation.cta.search')
      : t('eventBar.eventLocation.cta.accountPage');

  const [selectedOrderAccount, setSelectedOrderAccount] = useState(values.orderAccount);
  const tracker = useTracking();
  const analyticsSearchId = useAnalyticsSearchId();
  const {caterer} = useDerivedMenuData();
  const isAppBarLg = useIsAppBarLg();

  const strategyOptions = Object.keys(
    FulfillmentDetailStrategy,
  ) as (keyof typeof FulfillmentDetailStrategy)[];

  const {addresses: savedAddresses} = useSavedAddressSearch({
    searchTerm: ' ',
    limit: 2,
    corpAccountId: selectedOrderAccount?.id,
  });

  // Debounce to avoid double firing the "shown saved addresses" event
  // when the location dropdown is opened with saved addresses already loaded.
  useDebounce(
    () => {
      if (savedAddresses?.length > 0) {
        trackEvent('shown saved addresses', {}, {url: window.location.href});
      }
    },
    250,
    [savedAddresses],
  );

  // Track when multiple address matches are shown
  useEffect(() => {
    if (isAppBarLg && multipleAddresses && multipleAddresses.length > 1) {
      tracker.track('address_disambiguation_alert:shown', {
        search_id: analyticsSearchId ?? null,
        page: window.location.pathname,
        url: window.location.href,
        misc_json: JSON.stringify({
          fulfillment_detail_id: fulfillmentDetail?.id ?? null,
          number_of_matches: multipleAddresses.length,
          caterer_id: caterer?.id ?? null,
        }),
      });
    }
  }, [
    isAppBarLg,
    multipleAddresses,
    analyticsSearchId,
    fulfillmentDetail?.id,
    caterer?.id,
    tracker,
  ]);

  return (
    <Form className="mb-4 flex flex-col gap-4">
      {status?.error && (
        <Alert className="w-[350px]" variant="critical">
          <AlertTitle>{status.error.message}</AlertTitle>
        </Alert>
      )}
      {shouldRenderSelectOrderAccount && (
        <div className="flex flex-col gap-2">
          <label className="font-bold" htmlFor="event-location-order-account">
            {t('eventBar.eventLocation.orderAccount.label')}
          </label>
          <Select.Root
            items={corpAccountSelectItems}
            value={
              availableCorpAccountOptions.find(({id}) => id === values.orderAccount?.id)?.id ?? ''
            }
            onOpenChange={onSelectOpenChange}
            onValueChange={newId => {
              const option = availableCorpAccountOptions.find(({id}) => id === newId) ?? null;
              setFieldValue('orderAccount', option);
              setSelectedOrderAccount(option);
            }}
          >
            <Select.Trigger
              aria-label={t('eventBar.eventLocation.orderAccount.label')}
              className="min-w-80"
              id="app-bar-order-account"
              disabled={isSubmitting}
            >
              {values.orderAccount ? (
                <div className="flex items-center">
                  <Icon className="mr-2 text-peppercorn-800" icon={faBuildings} size="xsmall" />
                  <span className="text-peppercorn-800">{values.orderAccount.label}</span>
                </div>
              ) : (
                <Select.Value />
              )}
            </Select.Trigger>
            <Select.Content className="min-w-80" data-eventbar-ignore>
              {availableCorpAccountOptions.map(option => (
                <Select.Item key={option.id} value={option.id}>
                  {option.label}
                </Select.Item>
              ))}
            </Select.Content>
          </Select.Root>
        </div>
      )}
      <div className="flex flex-col gap-2">
        <div className="font-bold">{t('eventBar.eventLocation.orderType.label')}</div>

        {/* --- Order type --- */}
        <RadioGroup.Root
          className="flex gap-4"
          value={values.eventOrderType}
          onValueChange={value =>
            setFieldValue('eventOrderType', value as FulfillmentDetailStrategy)
          }
        >
          {strategyOptions.map(strategy => {
            const orderType = FulfillmentDetailStrategy[strategy];

            return (
              <Card
                render={
                  <label>
                    <RadioGroup.Item id={`event-order-type-${strategy}`} value={orderType} />
                    {t(`eventBar.eventLocation.orderType.${strategy}`)}
                  </label>
                }
                clickable
                className={twJoin(
                  'flex w-full cursor-pointer gap-2 p-3 hover:bg-peppercorn-50',
                  values.eventOrderType === orderType && 'font-bold',
                )}
                key={strategy}
              />
            );
          })}
        </RadioGroup.Root>
      </div>
      {/* --- Saved addresses --- */}
      {savedAddresses.length > 0 && (
        <div className="flex flex-col gap-2">
          <div className="font-bold">{t('eventBar.eventLocation.savedAddress.label')}</div>

          {savedAddresses.map(address => (
            <Card
              render={<button type="button" />}
              clickable
              className={twJoin(
                'flex w-full cursor-pointer items-center gap-4 p-3 hover:bg-peppercorn-50 active:border-peppercorn-300',
                values.savedAddress?.placeId === address.placeId &&
                  'border-ezgreen-400 ring-1 ring-ezgreen-400',
              )}
              key={address.placeId}
              onClick={() => {
                if (values.savedAddress?.placeId !== address.placeId) {
                  trackEvent(
                    'saved address selected',
                    {},
                    {
                      url: window.location.href,
                      search_keyword: address?.mainText || '',
                    },
                  );
                }

                setMultipleAddresses(null);
                setShowIncompleteAddressWarning(false);
                setFieldValue('savedAddress', address);
                setFieldValue('addressString', address.secondaryText);
                setFieldValue('googlePlace', null);
                fulfillmentDetailClickHandler(FulfillmentDetailStates.DATETIME);
              }}
            >
              <div className="rounded-xl bg-kiwi-50 p-3">
                <Icon icon={faBuilding} size="medium" />
              </div>
              <div className="flex w-3/4 flex-col text-left">
                <div className="font-bold">{address.mainText}</div>
                <div className="truncate font-normal">{address.secondaryText}</div>
              </div>
            </Card>
          ))}
        </div>
      )}
      {/* --- Event location --- */}
      <div className="flex flex-col gap-2">
        <label className="font-bold" htmlFor="event-location-address">
          {t('eventBar.eventLocation.addressSearch.label')}
        </label>

        {showIncompleteAddressWarning && (
          <Alert variant="critical">
            <AlertTitle>{t('eventBar.eventLocation.warning.incompleteAddress')}</AlertTitle>
          </Alert>
        )}

        <AddressSearchAutocomplete
          appBarStepThrough
          corpAccountFilterId={selectedOrderAccount?.id}
          geocodedAddressResultName="geocodedAddressResult"
          googlePlaceName="googlePlace"
          id="event-location-address"
          inputName="addressString"
          savedAddressName="savedAddress"
          shouldIncludeSavedAddresses
          trackingSubcategory="navigation bar"
          validateAddress={validateAddress}
        />

        <AddressUpdateListener
          setMultipleAddresses={setMultipleAddresses}
          setShowIncompleteAddressWarning={setShowIncompleteAddressWarning}
        />

        {multipleAddresses && multipleAddresses.length > 1 && (
          <div className="mt-1 flex flex-col gap-2">
            <Alert variant="critical">
              <AlertTitle>{t('eventBar.eventLocation.warning.multipleAddresses')}</AlertTitle>
            </Alert>

            <div className="flex flex-col gap-1">
              {multipleAddresses.map((multipleAddress, index) => (
                <Button
                  key={index}
                  className="border-peppercorn-200 font-normal"
                  data-testid="multiple-address-match"
                  onClick={() => {
                    setFieldValue('geocodedAddressResult', multipleAddress);
                    setFieldValue('addressString', getFullAddress(multipleAddress));
                    setFieldValue('googlePlace', null);
                    setMultipleAddresses(null);
                    fulfillmentDetailClickHandler(FulfillmentDetailStates.DATETIME);
                  }}
                  variant="outlined"
                >
                  <div className="text-center">{getFullAddress(multipleAddress)}</div>
                </Button>
              ))}
            </div>
          </div>
        )}
      </div>
      <Button
        className="w-full"
        disabled={hasMultipleAddresses || showIncompleteAddressWarning}
        loading={isSubmitting}
        type="submit"
      >
        {cta}
      </Button>
    </Form>
  );
};

export default Location;
